"use client";

import React, { useState, useRef, useEffect } from "react";
import { Send, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useChatStore } from "@/store/chat-store";
import { cn } from "@/lib/utils";

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
}

export function MessageInput({ onSendMessage, disabled = false }: MessageInputProps) {
  const [message, setMessage] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { isTyping } = useChatStore();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled && !isTyping) {
      onSendMessage(message.trim());
      setMessage("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && !isComposing) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleStop = () => {
    // In a real app, this would stop the AI generation
    useChatStore.getState().setIsTyping(false);
  };

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [message]);

  return (
    <div className="border-t border-slate-800 bg-slate-950 p-3 md:p-4">
      <form onSubmit={handleSubmit} className="relative">
        <div className="flex items-end gap-2">
          <div className="flex-1 relative">
            <Textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={() => setIsComposing(false)}
              placeholder={isTyping ? "AI yazıyor..." : "Mesajınızı buraya yazın..."}
              disabled={disabled || isTyping}
              className={cn(
                "min-h-[44px] max-h-[120px] resize-none pr-12",
                "focus-visible:ring-1 scrollbar-thin"
              )}
              rows={1}
            />

            {/* Send/Stop Button */}
            <div className="absolute bottom-2 right-2">
              {isTyping ? (
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  onClick={handleStop}
                  className="h-8 w-8"
                >
                  <Square className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  size="icon"
                  disabled={!message.trim() || disabled}
                  className="h-8 w-8"
                >
                  <Send className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Character count and hints */}
        <div className="flex justify-between items-center mt-2 text-xs text-slate-400">
          <span className="hidden sm:inline">Göndermek için Enter, yeni satır için Shift+Enter</span>
          <span className="sm:hidden">Enter: Gönder, Shift+Enter: Yeni satır</span>
          <span>{message.length}/2000</span>
        </div>
      </form>
    </div>
  );
}